import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, Shield, Wrench, Clock, DollarSign } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function SwiftCurrentReplacement() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof replacement cost in Swift Current?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Roof replacement in Swift Current typically ranges from $6,000 to $14,000 for an average home. Costs vary based on the square footage, roof pitch, and materials selected. We provide detailed, fixed-price quotes.",
        },
      },
      {
        "@type": "Question",
        name: "How do you handle the high winds in Swift Current?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We use high-wind rated architectural shingles and employ enhanced fastening techniques (often 6 nails per shingle instead of the standard 4) to ensure your roof stays secure during severe prairie windstorms.",
        },
      },
      {
        "@type": "Question",
        name: "How long will my new roof last?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "With our premium materials and expert installation, a new architectural shingle roof can last 25-30 years, while our metal roofing options can last 50 years or more, even in Swift Current's climate.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Swift Current",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Replacement",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Swift Current",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Swift Current",
        item: "https://www.saskroofingcompany.com/areas/swift-current",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Replacement",
        item: "https://www.saskroofingcompany.com/areas/swift-current/replacement",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Replacement Swift Current | Sask Roofing Company"
        description="Expert roof replacement in Swift Current, SK. We install wind-resistant, high-durability roofing systems built for prairie weather. Get a free quote today."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/58ccf1f5-72ed-44a9-9bc9-0564b2bc0915.png&output=webp&q=60"
              alt="Swift Current Roof Replacement"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Replacement Swift Current
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Southwestern Saskatchewan weather is relentless. When it's time
                to replace your Swift Current roof, you need a system engineered
                to handle high winds, severe hailstorms, and extreme
                temperatures. We provide honest pricing and expert local
                installation.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get Your Free Inspection</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  Signs You Need a Roof Replacement
                </h2>
                <p className="text-lg text-muted-foreground">
                  The strong winds and sudden storms in Swift Current can
                  accelerate roof aging. Catching the signs of a failing roof
                  early can save you thousands in interior water damage and
                  structural repairs.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    Don't ignore these warning signs:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Roof Age:</strong> If your roof is approaching
                        20 years old, the materials are likely degrading and
                        losing their weatherproofing capabilities.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Wind Damage:</strong> Missing, cracked, or
                        lifted shingles after one of Swift Current's notorious
                        windstorms.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wrench className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Granule Loss:</strong> Bald spots on your
                        shingles or excessive granules washing out of your
                        downspouts.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <DollarSign className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Sagging Roof Deck:</strong> A wavy or drooping
                        roofline indicates severe structural moisture damage
                        requiring immediate replacement.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/fe7242f1-b408-4417-8330-aaae1161669d.png&output=webp&q=60"
                  alt="Roof installation in Swift Current"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Built for Swift Current Roof Replacement Saskatchewan
              </h2>
              <p className="text-lg text-muted-foreground">
                A standard roof won't survive the specific climate challenges of
                Swift Current. We install robust roofing systems designed to
                conquer the prairies.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Extreme Wind Resistance
                </h3>
                <p className="text-muted-foreground">
                  We utilize high-wind rated shingles and enhanced fastening
                  patterns to ensure your roof stays firmly attached, even when
                  the prairie winds exceed 100 km/h.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Hail Impact Protection
                </h3>
                <p className="text-muted-foreground">
                  Summer storms in the southwest often bring damaging hail. We
                  offer impact-resistant shingle options that can withstand
                  severe weather and potentially lower your insurance premiums.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Thermal Shock Defense
                </h3>
                <p className="text-muted-foreground">
                  Rapid temperature swings cause roofing materials to expand and
                  contract. Our premium underlayments and proper ventilation
                  systems prevent premature cracking and aging.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Swift Current
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Swift Current, a standard residential
                  roof replacement (where we are simply tearing off old shingles
                  and installing new ones over existing decking) typically does
                  not require a building permit. However, if your roof has
                  suffered structural damage—such as broken trusses from heavy
                  snow loads or rotted decking that needs extensive
                  replacement—a permit from the City of Swift Current may be
                  required. We assess your roof's structural integrity during
                  our free inspection and manage all necessary permitting and
                  compliance, ensuring your new roof meets all local and
                  provincial building codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Swift Current
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Swift Current is no stranger to severe summer hailstorms and
                  high wind events that can devastate a roof in minutes. If your
                  roof has been damaged by a storm, dealing with insurance can
                  be stressful. We specialize in storm damage restoration and
                  frequently work with local insurance adjusters. If you suspect
                  damage, call us for a free inspection before filing a claim.
                  We will document the damage with detailed photos and provide a
                  comprehensive report for your adjuster, ensuring you get the
                  full coverage you are entitled to for a proper roof
                  replacement.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Swift Current Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Swift Current's demanding climate, we typically recommend
                  premium architectural shingles from trusted manufacturers like
                  IKO, CertainTeed, or GAF. Specifically, we often suggest Class
                  4 impact-resistant shingles to combat the frequent hailstorms
                  in the region. These shingles are engineered to withstand
                  severe weather and come with robust manufacturer warranties.
                  When combined with our local workmanship warranty, you get a
                  roofing system that provides long-lasting peace of mind
                  against the harsh Saskatchewan elements.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      150+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Roofs in Swift Current
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Class 4
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Impact Resistance
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      130km/h
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Wind Rating
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/fe7242f1-b408-4417-8330-aaae1161669d.png&output=webp&q=60"
                  alt="Beautiful completed residential roof in Swift Current"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">How We Replace Your Roof</h2>
                <p className="text-lg text-muted-foreground">
                  Our process is designed to be efficient, transparent, and
                  completely stress-free for Swift Current homeowners.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Detailed Inspection
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We evaluate your roof's condition, taking note of
                        specific wind or hail damage, and check your attic's
                        ventilation setup.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Honest, Fixed Pricing
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        You get a clear, written estimate detailing the
                        materials and labor. The price we quote is the price you
                        pay.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Professional Installation
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        Our skilled crews perform a full tear-off, repair any
                        damaged decking, and install your new wind-resistant
                        roofing system.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Complete Cleanup
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We respect your property. We protect your landscaping
                        during the job and use magnetic sweepers to ensure no
                        nails are left behind.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Swift Current roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Swift Current:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/swift-current"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Swift Current roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/swift-current/repair"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Repair in Swift Current
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Swift Current Roof Replacement FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof replacement cost in Swift Current?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Roof replacement in Swift Current typically ranges from $6,000
                  to $14,000 for an average home. Costs vary based on the square
                  footage, roof pitch, and materials selected. We provide
                  detailed, fixed-price quotes so there are no surprises.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How do you handle the high winds in Swift Current?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  We use high-wind rated architectural shingles and employ
                  enhanced fastening techniques (often 6 nails per shingle
                  instead of the standard 4) to ensure your roof stays secure
                  during severe prairie windstorms.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How long will my new roof last?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  With our premium materials and expert installation, a new
                  architectural shingle roof can last 25-30 years, while our
                  metal roofing options can last 50 years or more, even in Swift
                  Current's demanding climate.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Ready for a Roof That Lasts?
            </h2>
            <p className="text-xl opacity-90">
              Get a free, no-obligation inspection and a fixed-price quote from
              Swift Current's roofing experts.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
